import { markdownToHtmlPreservingBlanks } from "@components/design-system/molecules/richTextFormatUtils";
import type { PimsExportFormat } from "@hooks/use-pims-export-format";
import { stripNbsp } from "@src/utils";
import { stripMarkdown } from "@src/utils/text";

/**
 * Computes the clipboard payload for a given markdown string and PIMS format.
 * Returns `{ plain, html }` where `html` is empty string when not applicable.
 *
 * Use this in synchronous copy event handlers (`event.clipboardData.setData`).
 * For button-triggered copies use `writeToClipboardWithPimsFormat` instead.
 */
export function getClipboardPayload(
  markdownText: string,
  format: PimsExportFormat,
): { plain: string; html: string } {
  // Normalise &nbsp; once up-front for all formats.
  // (stripMarkdown also strips &nbsp; for callers that use it standalone.)
  const clean = stripNbsp(markdownText);

  if (format === "plain_text") {
    return { plain: stripMarkdown(clean), html: "" };
  }
  if (format === "markdown") {
    return { plain: clean, html: "" };
  }
  // rich_text
  return {
    plain: clean,
    html: markdownToHtmlPreservingBlanks(clean),
  };
}

/**
 * Writes text to the clipboard according to the user's PIMS export format setting.
 *
 * - plain_text: strips markdown symbols, writes as plain text
 * - markdown:   writes markdown symbols as-is, as plain text
 * - rich_text:  converts markdown to HTML, writes both text/html and text/plain
 *
 * Pass `formatOverride` to bypass the localStorage lookup (e.g. when the
 * caller has already read the format and pre-processed the text accordingly).
 */
export async function writeToClipboardWithPimsFormat(
  markdownText: string,
  format: PimsExportFormat,
): Promise<void> {
  const { plain, html } = getClipboardPayload(markdownText, format);

  if (html) {
    // ClipboardItem may throw synchronously in some browsers/contexts.
    // Fall back to plain text if it's unavailable.
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
    } catch (err) {
      console.warn(
        "Rich text clipboard write failed, falling back to plain text:",
        err,
      );
      await navigator.clipboard.writeText(plain);
    }
  } else {
    await navigator.clipboard.writeText(plain);
  }
}
